import * as shvl from 'shvl';
import Vue from 'vue';
import VueCookies from 'vue-cookies';
import hmacSHA256 from 'crypto-js/hmac-sha256';
import MD5 from 'crypto-js/md5';
import SHA256 from 'crypto-js/sha256';
import { loadJS } from '@/libs/async-load.js';
import { formatImageSrc } from '@xd-rocket/utils/base';
import { initDeviceOrientationEvent } from './device-orientation';
Vue.use(VueCookies, { expire: '7d' });

// todo 节点相关 api, 暂时放在这里

/**
 * 检测元素是否在限制范围以外。
 * 当元素不在列表中时，返回 false, 代表不需要限制
 * 或者当元素在列表中的索引值与索引列表一致时，返回 false，表示在限制的范围以内
 * 当元素在列表中的索引值与索引列表不一致，返回 true， 表示在限制范围以外
 * @param {object} indexs 索引列表，类型为 对象，每一项由 列表 id 及 索引 组成，格式 { [列表 id]: index}
 * @param {object} node 元素，当元素的 dataInject 不存在时，不进行检测
 * @returns {boolean} 为真 代表检测到元素在列表中，且在限制范围以外。为假，代表不需要限制或者在限制范围内
 */
export function checkLimit(indexs, node) {
  if (!indexs || !node.values.dataInject) return false;
  let dataInject = node.values.dataInject;
  try {
    let result = Object.keys(indexs).find(
      (i) => indexs[i] !== undefined && indexs[i] != dataInject[i]?.path
    );
    if (result) return true;
  } catch (e) {}

  return false;
}

/**
 * 获取元素在指定列表中的组号
 * @param {*} node 元素
 * @param {*} info 列表相关信息 { key: [列表id], limit:{ [父级列表 id]: [限制的组号]}
 * @returns 组号，从 0 开始计数
 */
export function getNodeIndexWithList(node, info) {
  if (!node?.values?.dataInject || !info?.key) return -1;
  if (info.limit) {
    if (checkLimit(info.limit, node)) return -1;
  }
  let listId = info.key;
  let obj = node.values.dataInject[listId];
  if (obj === undefined) return -1;
  return obj.path;
}

export function isJson(value) {
  const type = typeof value;
  return value != null && type === 'object';
}

// 文本转化
export function parse(str = '') {
  try {
    return JSON.parse(str);
  } catch (error) {
    return {};
  }
}

export function hasOwnProperty(value, key = '') {
  try {
    if (!value || !key) return false;
    return !!value?.hasOwnProperty(key);
  } catch (error) {
    return {};
  }
}

export function getJsonKeys(value) {
  try {
    if (isJson(value)) return Object.keys(value);
    else return [];
  } catch (error) {
    return [];
  }
}

export function getJsonValues(value) {
  try {
    if (isJson(value)) return Object.values(value);
    else return [];
  } catch (error) {
    return [];
  }
}

export function getListIntermingle(value) {
  try {
    if (value && value.length > 0) {
      const arr = [...value];
      arr.sort(() => 0.5 - Math.random());
      return arr;
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
}

/**
 * 解析给定的链接，根据指定的 key 返回对应的结果，如果未指定 key, 返回 URL 对象
 * @param {string} url 绝对或相对 URL
 * @param {string} key 想要获得的属性，例 pathname, origin, hash 等，如果没有，会返回 URL 对象本身，支持的属性见：https://developer.mozilla.org/zh-CN/docs/Web/API/URL
 * @param {string} base 如果给定的 url 是相对链接，需要传入域名，否则默认为当前网址的域名
 * @returns
 */
export function parseUrl(url, key, base) {
  try {
    if (!url) url = window.location.href;
    let result = new URL(url, base || window.location.origin);
    if (result[key]) return result[key];
    return result;
  } catch (error) {
    console.log(error);
  }
}

// 设备 - 解析URL地址，获取参数
export function searchParams(url, key) {
  if (!url || !key) return;
  try {
    let params = parseUrl(url).searchParams;
    let v = params.get(key);
    if (v) return v;
    else return;
  } catch (error) {
    return;
  }
}

// 设备 - 设置URL地址参数为新值
export function changeSearchParams(url, key, newValue) {
  if (!url || !key || !newValue) return;
  try {
    const currentUrl = new URL(url);
    const searchParams = currentUrl.searchParams;
    searchParams.set(key, newValue);
    const newUrl = currentUrl.toString();
    if (newUrl) return newUrl;
    else return;
  } catch (error) {
    return;
  }
}

// 设备 - 修改网址字符串
export function historyReplaceState(url, status) {
  if (typeof history === 'undefined' || !url || !status) return;
  try {
    // console.log(history);
    history.replaceState({ key: status }, '', url);
  } catch (error) {
    return;
  }
}

// 设备 - 获取本地数据
export function getLocalData(key) {
  if (typeof window === 'undefined') return;

  let data = localStorage.getItem(key);
  return JSON.parse(data);
}

/**
 * 移除localStorage数据
 * @param {*} key string
 * @returns
 */
export function removeLocalData(key) {
  if (typeof window === 'undefined') return;

  localStorage.removeItem(key);
}

// 设备 - 获取本地凭证
export function getCookie(key) {
  let data = Vue.$cookies.get(key);
  if (data && (data.substr(0, 1) === '{' || data.substr(0, 1) === '[')) {
    try {
      data = JSON.parse(data);
    } catch (error) {}
  }
  return data;
}

/**
 * 移除cookie数据
 * @param {*} key string
 * @returns
 */
export function removeCookie(key) {
  if (typeof window === 'undefined') return;

  Vue.$cookies.remove(key);
}

// 时间 - 通过日期字符串获取时间戳
export function getClientTimeByDate(value) {
  if (typeof window === 'undefined') return;
  return new Date(value).getTime();
}

// 设置时间
export function setTime(year, month, day, hour, minute, second, timestamp) {
  let dateStr = `${year}-${month}-${day}T${hour}:${minute}:${second}+0800`;
  if (timestamp) {
    let data = new Date(dateStr);
    if (!data || data == 'Invalid Date') return;

    return data.getTime();
  } else {
    return dateStr;
  }
}

// 日期格式化
function formatNum(n) {
  return n < 10 ? '0' + n : '' + n;
}
export function customizeFormatTime(
  time,
  year_cut,
  month_cut,
  hour_cut,
  minute_cut,
  show_year,
  show_month,
  show_day,
  show_hour,
  show_minute,
  show_second,
  joiner_cut
) {
  let date = new Date(time);
  if (!date || date == 'Invalid Date') return;

  let _year = date.getFullYear(),
    _month = date.getMonth() + 1,
    _day = date.getDate(),
    _hour = date.getHours(),
    _minute = date.getMinutes(),
    _second = date.getSeconds();

  let year = show_year ? (show_month ? _year + year_cut : _year) : '',
    month = show_month
      ? show_day
        ? formatNum(_month) + month_cut
        : formatNum(_month)
      : '',
    day = show_day ? formatNum(_day) : '',
    hour = show_hour
      ? show_minute
        ? formatNum(_hour) + hour_cut
        : formatNum(_hour)
      : '',
    minute = show_minute
      ? show_second
        ? formatNum(_minute) + minute_cut
        : formatNum(_minute)
      : '',
    second = show_second ? formatNum(_second) : '';

  let joiner = joiner_cut || '  ';

  let time_formated = `${year}${month}${day}${joiner}${hour}${minute}${second}`;
  return time_formated.replace(/^\s+|\s+$/gm, '');
}

// 来自事件编辑器自定义事件
export function receiveCustomEvent(name, callback, once) {
  if (!name) return;
  let type = once ? '$once' : '$on';
  window.moo.$bus[type](name, callback);
}

export function sendCustomEvent(name, data) {
  if (!name) return;
  window.moo.$bus.$emit(name, data);
}

export function triggerNodeEvent(eventName, limit, ...args) {
  if (!limit?.key || !eventName) {
    let values = limit.values;
    if (!values || !values.id || !values?.parent) {
      return;
    }
    let obj = { key: values.id };
    let dataInject = limit?.values?.dataInject;
    if (dataInject) {
      obj.limit = {};
      for (let i in dataInject) {
        obj.limit[i] = dataInject[i].path;
      }
    }
    limit = obj;
  }
  window.moo.$bus.$emit(
    'rocket:node:trigger:' + limit.key,
    eventName,
    limit,
    ...args
  );
}

// 编码/解码
export function textEncode(str) {
  return encodeURIComponent(str);
}
export function textDecode(str) {
  return decodeURIComponent(str);
}

// 将文本当作 js 代码执行
export function evalStr(str) {
  try {
    // eslint-disable-next-line no-new-func
    return new Function(str)();
  } catch (e) {
    console.error('事件块中，将字符串当作 js 代码执行遇到报错:', e);
  }
}

//字符串加密
export function textCrypto(value, type) {
  //加密算法按需引入
  switch (type) {
    case 'MD5':
      return MD5(value).toString();
    case 'SHA256':
      return SHA256(value).toString();
    default:
      return value.toString();
  }
}

//HmacSHA256加密
export function textHmacSHA256(value, key) {
  return hmacSHA256(value, key).toString();
}

/**
 * 正则表达式校验字符串是否合法
 * @param {string} pattern 正则表达式
 * @param {string} value 需要校验的字符串
 * @param {boolean} isCaseSensitive 是否区分大小写
 * @returns boolean
 */
export function regexTest(pattern, value, isCaseSensitive) {
  const regex = new RegExp(pattern, isCaseSensitive ? '' : 'i');
  return regex.test(value);
}

/**
 * 按长度截断文字，支持中文，并添加省略号（分享时不支持文字截断）
 * 一个中文字符占位两个宽度
 * @param {string} str 文本
 * @param {number} len 最大长度
 * @returns {string} 截断后的文本
 */
export function beautySub(str, len) {
  let result = [];
  let count = 0;
  let arr = (str = str.split(''));
  let max = Math.min(str.length, len);
  for (var i = 0; i < max; i++) {
    let reg = /[\u4E00-\u9FA5\u3000-\u303F\uFF01-\uFF5E]/;
    let char = arr[i];
    let isChinese = reg.test(char);
    if (isChinese) {
      count = count + 2;
    } else {
      count = count + 1;
    }
    if (count >= len) {
      if (count === len && i === str.length - 1) {
        result.push(char);
      } else {
        result.push('...');
      }
      break;
    } else {
      result.push(char);
    }
  }
  return result.join('');
}

/**
 * 当文本超过n行时 截断文字 并添加省略号（分享时不支持文字截断）
 * 一个中文字符占位两个宽度
 * @param {string} str 文本
 * @param {number} lines 限制行数
 * @param {number} len 每行最多显示的字符数
 * @returns {string} 截断后的文本
 */
export function truncateTextByLines(str, lines, len) {
  let result = '';
  let currentWidth = 0;
  let currentLines = 0;

  for (let char of str) {
    if (currentWidth + (encodeURI(char).length > 4 ? 2 : 1) <= len) {
      result += char;
      currentWidth += encodeURI(char).length > 4 ? 2 : 1;
    } else {
      result += char + '\n';
      currentWidth = 0;
      currentLines++;
    }
    if (currentLines >= lines) {
      result = result.slice(0, -2);
      result += '...';
      return result;
    }
  }
  return result;
}

// 创建anime动画
// function loadJS(url, callback) {
//   let script = document.createElement('script'),
//     fn = callback || function () {};
//   script.type = 'text/javascript';

//   //IE
//   if (script.readyState) {
//     script.onreadystatechange = function () {
//       if (script.readyState == 'loaded' || script.readyState == 'complete') {
//         script.onreadystatechange = null;
//         fn();
//       }
//     };
//   } else {
//     //其他浏览器
//     script.onload = function () {
//       fn();
//     };
//   }
//   script.src = url;
//   document.getElementsByTagName('head')[0].appendChild(script);
// }

export async function createAnime(options) {
  if (typeof window === 'undefined') return;
  // if (!window?.anime) {
  //   try {
  //     // loadJS(
  //     //   'https://cdnjs.cloudflare.com/ajax/libs/animejs/3.2.1/anime.min.js',
  //     //   () => {
  //     //     startCreate(options);
  //     //   }
  //     // );
  //   } catch (e) {
  //     console.warn(e);
  //   }
  // } else {
  //   startCreate(options);
  // }
  await loadJS('https://poster.xdcdn.net/poster/.system/js/animejs-3.2.1.js');
  startCreate(options);
  return options;
}

let ANIME_OBJ = {};
function startCreate(options) {
  if (!options) return;

  try {
    const subAnime = window.anime.timeline({
      autoplay: options.autoplay,
      loop: options.loop,
    });

    if (options.animeList) {
      options.animeList.forEach((item) => {
        if (item) {
          subAnime.add(item, item.anime_offset);
        }
      });
    }

    ANIME_OBJ[options.name] = subAnime;
  } catch (e) {
    console.warn(e);
  }
}

// 播放动画
export function controlAnime(control, name) {
  if (!name || !ANIME_OBJ[name]) {
    console.warn(`未找到动画${name}`);
    return;
  }

  switch (control) {
    case 'play':
      ANIME_OBJ[name].play();
      break;
    case 'pause':
      ANIME_OBJ[name].pause();
      break;
    case 'restart':
      ANIME_OBJ[name].restart();
      break;
    case 'reverse':
      ANIME_OBJ[name].reverse();
      break;
  }
}

/**
 * 获取元素的属性
 * @param {*} node 元素
 * @param {*} name 属性名称
 * @returns 属性值
 */
export function getNodeAttribute(node, name) {
  return shvl.get(node, name);
}

export function getMooAttribute(name) {
  return shvl.get(window.moo, name);
}
export function getDeviceType(name) {
  return shvl.get(window.moo.CONSTANT, name);
}
// 多语言数据合集
export function getLanguageInfo(name) {
  if (name === 'browserLanguage') {
    return (
      (window.navigator &&
        (window.navigator.language || window.navigator.browserLanguage)) ||
      ''
    )
      .split('-')[1]
      .toLowerCase();
  }
  return shvl.get(window.moo, name);
}

/**
 * 获取表单元素的值
 * @param {*} node 元素
 * @returns String | Number
 */
export function getFormValue(node) {
  const el = node.$el || {};
  const type = node.values.type;
  let value = el.value;
  if (type === 'WidgetInput' && node.values.inputType === 'number') {
    value = Number(value);
  }
  return value;
}

/**
 * 获取表单元素的值
 * @param {*} node 元素
 * @returns String | Number
 */
export function getInputValue(node) {
  if (!node?.key) return;

  const handler = (el) => {
    if (!el) return;

    const tagName = el.tagName.toUpperCase();
    let value = '';
    if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
      value = el.value;
      if (el.type === 'number') {
        value = Number(value);
      }
    }
    return value;
  };

  // 通过ID获取元素，单元素的情况
  const elById = document.getElementById(node.key);
  if (elById) {
    return handler(elById);
  }

  // 通过className获取元素，元素处于列表中的情况
  let index;
  let parentEl = document;

  if (node?.limit) {
    const limitArr = Object.entries(node.limit);
    const limitEl = limitArr[0] || [];
    index = limitEl[1];
    parentEl = document.querySelector('.' + limitEl[0]);
  }

  const elsByClass = parentEl.getElementsByClassName(node.key) || [];

  if (index !== undefined) {
    return handler(elsByClass[index]);
  }

  const values = [];
  for (const elByClass of elsByClass) {
    values.push(handler(elByClass));
  }

  return values;
}

/**
 * 图片预加载
 * @param {*} name 名称
 * @param {*} list 图片列表
 */
export function imagePreload(name, list) {
  setTimeout(() => {
    let el_list = [];
    let inside_list = [];
    const reg =
      /https:\/\/((?!https:\/\/).)+?(\.jpg|\.png|\.jpeg|\.gif|\.svg)/i;
    list = list.filter((item) => {
      if (
        item !== null &&
        typeof item == 'object' &&
        item.key &&
        document.getElementById(item.key)
      ) {
        el_list.push(document.getElementById(item.key));
      }
      if (Array.isArray(item)) {
        item.forEach((v) => {
          if (v !== null && typeof v == 'string' && v.match(reg)) {
            inside_list.push(v);
          }
        });
      }
      return item !== null && typeof item == 'string' && item.match(reg);
    });
    list = list.concat(inside_list);
    if (el_list.length > 0) {
      el_list.forEach((el) => {
        const _url = getElImgUrl(el);
        if (_url) {
          list.push(_url);
        }
        for (let obj of el.getElementsByTagName('*')) {
          if (!obj.id) continue;
          const _temp = getElImgUrl(obj);
          if (_temp) {
            list.push(_temp);
          }
        }
      });
      startPreload(name, list);
    } else {
      startPreload(name, list);
    }
  }, 50);
}

function getElImgUrl(obj) {
  const src = obj.getAttribute('src') || obj.children?.item(1)?.src;
  let ret = '';
  if (src) {
    ret = src;
  } else {
    const bg = document.defaultView.getComputedStyle(obj)['background-image'];
    if (bg !== 'none' && bg.indexOf('url(') > -1) {
      ret = bg.match(/url\("([\s\S]*?)"\)/)[1];
    }
  }
  if (
    ret.match(/https:\/\/((?!https:\/\/).)+?(\.jpg|\.png|\.jpeg|\.gif|\.svg)/i)
  ) {
    return ret;
  }
}

function startPreload(name, list) {
  if (list.length === 0) {
    return;
  }
  //去重
  list = Array.from(new Set(list));
  // console.log('preload :', list);
  let resSum = 0;
  let resLoaded = 0;

  for (let i in list) {
    let src = list[i];
    if (!src) {
      continue;
    }
    let image = new Image();
    let srcInfo = formatImageSrc(src, window.moo.ossImgRatio);

    if (window.devicePixelRatio === 1 && image.isValid) {
      image.src = srcInfo.src_1x;
    } else {
      image.src = srcInfo.src;
    }

    resSum++;
    if (image.complete) {
      resLoaded++;
      updateProgress(resLoaded, resSum, name);
      continue;
    }
    image.onload = () => {
      resLoaded++;
      updateProgress(resLoaded, resSum, name);
    };
  }
}

function updateProgress(resLoaded, resSum, name) {
  let loading_progress = 0;
  let progress = Math.ceil((100 * resLoaded) / resSum);
  if (progress > loading_progress) {
    loading_progress = progress;
  }
  window.moo.$bus.$emit(
    'rocket:image:preload-progress:' + name,
    loading_progress
  );
  if (loading_progress >= 100) {
    window.moo.$bus.$emit('rocket:image:preload-success:' + name);
  }
}

let gameLauncherIpc = undefined;

function getIPC() {
  const gameLauncherIpc = window['ipcMessage'];
  if (gameLauncherIpc) {
    return gameLauncherIpc;
  } else {
    console.warn('Takes effect only in electron');
    return false;
  }
}

let temp_time = 0;

export function sendLauncherMessage(action, message = '') {
  // console.log(action);
  if (!gameLauncherIpc) {
    gameLauncherIpc = getIPC();
    if (!gameLauncherIpc) return;

    gameLauncherIpc.get((_event, value) => {
      let { action, message, status } = value;

      // console.log('receiveMessage', value);
      window.moo.$bus.$emit('rocket:gameLauncher:get', action, message, status);
    });

    // console.log('sendMessage', { action, message });
    gameLauncherIpc.send({ action, message });
  } else {
    // console.log('sendMessage', { action, message });
    gameLauncherIpc.send({ action, message });
  }
}

export { shvl, initDeviceOrientationEvent };
